const memoizeFormatConstructor = require('intl-format-cache').default;
const getNumberFormat = memoizeFormatConstructor(Intl.NumberFormat);
const getDateTimeFormat = memoizeFormatConstructor(Intl.DateTimeFormat);
const {
    getSanitizedLocaleForIntl
} = require('./localeUtils');

const I18nFormatter = {
    /**
     * Formats the specified amount for the given locale and currency.
     * This logic was taken from https://github.homeawaycorp.com/hacommons/hacommons-i18n/blob/master/src/main/java/com/homeaway/commons/i18n/formatter/GlobalCurrencyFormatter.java#L239
     *
     * @param value the number value to format
     * @param currency the currency code for the currency you wish to add (eg 'USD', 'AUD', 'GBP')
     * @param locale the locale to determine the format of the result (defaults to 'en_US')
     * @param maximumFractionDigits the maximum number of fraction digits to keep
     * @param minimumFractionDigits the minimum number of fraction digits to keep
     * @param useGrouping whether to use separator between thousands
     * @param style whether to format `currency` or `decimal`
     * @returns The formatted amount with correct number format based on the locale and currency
     */
    formatNumber({
        value,
        currency,
        locale = 'en_US',
        maximumFractionDigits,
        minimumFractionDigits,
        useGrouping,
        style
    }) {
        const options = {
            currency,
            currencyDisplay: 'code',
            maximumFractionDigits,
            minimumFractionDigits,
            style,
            useGrouping
        };
        const formattedCurrency = getNumberFormat(
            getSanitizedLocaleForIntl(locale),
            options
        ).format(value);

        return formattedCurrency;
    },

    /**
     * Formats the specified date for the given locale and date.
     *
     * @param date the date value to format
     * @param weekday the format of weekday you wish to get (eg 'narrow', 'short', 'long')
     * @param era the era format for the date
     * @param year the format of year you wish to get (eg 'numeric', '2-digit')
     * @param month the format of month you wish to get (eg 'numeric', '2-digit', 'narrow', 'short', 'long')
     * @param day the format of day you wish to get (eg 'numeric', '2-digit')
     * @param hour the format of hour you wish to get (eg 'numeric', '2-digit')
     * @param minute the format of minute you wish to get (eg 'numeric', '2-digit')
     * @param second the format of second you wish to get (eg 'numeric', '2-digit')
     * @param timeZone timeZone for the date object
     * @param locale the locale to determine the format of the result, default value is 'en_US'
     * @returns The formatted string with correct date format based on the locale
     */
    formatDateTime({
        date,
        weekday,
        era,
        year,
        month,
        day,
        hour,
        minute,
        second,
        timeZone,
        locale = 'en_US'
    }) {
        const options = {
            weekday,
            era,
            year,
            month,
            day,
            hour,
            minute,
            second,
            timeZone
        };
        return getDateTimeFormat(
            getSanitizedLocaleForIntl(locale),
            options
        ).format(date);
    }
};

module.exports = {
    I18nFormatter
};
